import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, ChevronUp, ChevronDown } from "lucide-react";

const pad2 = (n) => String(n).padStart(2, "0");
const clamp = (n, max) => Math.min(max, Math.max(0, n));

export default function DateTimeField({ value, onChange, testid = "dt" }) {
  const [open, setOpen] = useState(false);
  const [datePart = "", timePart = ""] = (value || "").split("T");
  const [hh = "", mm = ""] = (timePart || "").split(":");
  const selected = datePart ? new Date(`${datePart}T12:00:00`) : undefined;

  const todayStr = () => {
    const n = new Date();
    return `${n.getFullYear()}-${pad2(n.getMonth() + 1)}-${pad2(n.getDate())}`;
  };
  const emit = (dp, h, m) => onChange(`${dp || todayStr()}T${pad2(clamp(h, 23))}:${pad2(clamp(m, 59))}`);

  const pickDate = (d) => {
    if (!d) return;
    emit(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
      parseInt(hh || "12", 10), parseInt(mm || "0", 10));
    setOpen(false);
  };
  const stepHour = (d) => emit(datePart, (parseInt(hh || "12", 10) + d + 24) % 24, parseInt(mm || "0", 10));
  const stepMin = (d) => emit(datePart, parseInt(hh || "12", 10), (parseInt(mm || "0", 10) + d + 60) % 60);
  const typeHour = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 2);
    if (d !== "") emit(datePart, clamp(parseInt(d, 10), 23), parseInt(mm || "0", 10));
  };
  const typeMin = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 2);
    if (d !== "") emit(datePart, parseInt(hh || "12", 10), clamp(parseInt(d, 10), 59));
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testid}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="w-full justify-start gap-2 font-normal" data-testid={`${testid}-date-btn`}>
            <CalendarDays className="h-4 w-4 text-emerald-600" />
            {selected
              ? selected.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "long", year: "numeric" })
              : "Choisir la date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={selected} defaultMonth={selected} onSelect={pickDate} data-testid={`${testid}-calendar`} />
        </PopoverContent>
      </Popover>
      <div className="flex items-center gap-1.5">
        <div className="flex flex-col items-center">
          <button type="button" onClick={() => stepHour(1)} aria-label="Augmenter les heures"
            className="text-emerald-700 hover:bg-emerald-50 rounded p-0.5" data-testid={`${testid}-hour-up`}>
            <ChevronUp className="h-4 w-4" />
          </button>
          <Input value={hh} onChange={(e) => typeHour(e.target.value)} inputMode="numeric" placeholder="HH"
            className="w-12 text-center px-1" data-testid={`${testid}-hour-input`} aria-label="Heures" />
          <button type="button" onClick={() => stepHour(-1)} aria-label="Diminuer les heures"
            className="text-emerald-700 hover:bg-emerald-50 rounded p-0.5" data-testid={`${testid}-hour-down`}>
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <span className="font-bold text-slate-500 pb-0.5">:</span>
        <div className="flex flex-col items-center">
          <button type="button" onClick={() => stepMin(5)} aria-label="Augmenter les minutes"
            className="text-emerald-700 hover:bg-emerald-50 rounded p-0.5" data-testid={`${testid}-min-up`}>
            <ChevronUp className="h-4 w-4" />
          </button>
          <Input value={mm} onChange={(e) => typeMin(e.target.value)} inputMode="numeric" placeholder="MM"
            className="w-12 text-center px-1" data-testid={`${testid}-min-input`} aria-label="Minutes" />
          <button type="button" onClick={() => stepMin(-5)} aria-label="Diminuer les minutes"
            className="text-emerald-700 hover:bg-emerald-50 rounded p-0.5" data-testid={`${testid}-min-down`}>
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
        <span className="text-xs text-slate-400 ml-1">h (saisie ou flèches ±5 min)</span>
      </div>
    </div>
  );
}
